import { Button } from '@acpaas-ui/react-components';
import { get } from 'lodash';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { fetchReset } from '../../actions/fetch.actions';
import { BRANCH_FIELDS } from '../../constants/admin.schools.constants';
import { MODALS } from '../../constants/modals.constants';
import {
  ADMIN_ORGANIZATIONS_PATHS,
  ADMIN_PATH,
  ADMIN_SCHOOLS_PATHS,
  ADMIN_TAB_PATHS
} from '../../constants/paths.constants';
import { useDependency } from '../../hooks/useDependency.hook';
import { useForm } from '../../hooks/useForm.hook';
import { useModal } from '../../hooks/useModal.hook';
import DeleteWarningModal from '../../modals/DeleteWarningModal';
import { ADMIN_SCHOOL_AGODI_BRANCHES_RESOURCE } from '../../resources/admin/admin.school.agodi.branches.resource';
import adminSchoolsService from '../../services/api/admin.schools.service';
import contactsService from '../../services/api/contacts.service';
import AdminSchoolDetailsField from './AdminSchoolDetailsField';

const AdminBranchDetails = ({ branch, canDelete, params }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const agodiData = branch?.spoEntityData?.agodi;
  const shadowData = branch?.spoEntityData?.shadow ?? {};
  const metaData = branch?.spoEntityMeta ?? {};
  const deleteModal = useModal(MODALS.DELETE_BRANCH_MODAL);
  const cityDistrictManagers = useDependency(contactsService.getCityContactsWithoutParents, {
    mapResponse: (items) =>
      items?.map((i) => {
        return {
          label: `${i?.spoContactData?.firstName} ${i?.spoContactData?.lastName}`,
          value: i?.id
        };
      })
  });
  const selectFieldsOptions = {
    cityDistrictManager: cityDistrictManagers.options
  };
  const { isSubmitting, data, error, reset, input, submitStart, submitOk, submitError } = useForm({
    ...agodiData,
    ...shadowData,
    isVisible: metaData.isVisible
  });
  const resource = {
    isFetching: false,
    isSubmitting,
    getValue: (field) => data?.[field],
    setValue: (value, field, options = {}) => {
      submitStart();
      adminSchoolsService.updateBranch(
        params.netId,
        params.managementId,
        params.communityId,
        params.schoolId,
        params.branchId,
        { [field]: value },
        {
          onSuccess: () => {
            submitOk();
            input(value, field);
            dispatch(fetchReset(ADMIN_SCHOOL_AGODI_BRANCHES_RESOURCE));
            ReactTooltip.rebuild();
            options.onSuccess();
          },
          onError: (message) => {
            submitError({ [field]: message });
            options.onError(message);
          }
        }
      );
    },
    validators: {},
    onCancel: reset
  };

  const handleDelete = (onError, onSuccess) => {
    adminSchoolsService.removeBranch(
      params.netId,
      params.managementId,
      params.communityId,
      params.schoolId,
      params.branchId,
      {
        onError,
        onSuccess: () => {
          onSuccess();
          navigate(
            generatePath(
              `${ADMIN_PATH}${ADMIN_TAB_PATHS.ORGANIZATIONS.path}${ADMIN_ORGANIZATIONS_PATHS.SCHOOLS.path}${ADMIN_SCHOOLS_PATHS.BRANCHES.path}`,
              params
            )
          );
        }
      }
    );
  };

  const handleVisibilityChange = (value) => {
    reset();
    const options = {
      onSuccess: () => {
        submitOk();
        input(value, 'isVisible');
      },
      onError: (message) => {
        submitError({ isVisible: message });
      }
    };
    submitStart();
    if (!!value) {
      adminSchoolsService.setBranchVisible(
        params.netId,
        params.managementId,
        params.communityId,
        params.schoolId,
        params.branchId,
        options
      );
    } else {
      adminSchoolsService.setBranchInvisible(
        params.netId,
        params.managementId,
        params.communityId,
        params.schoolId,
        params.branchId,
        options
      );
    }
  };

  useEffect(() => {
    if (!cityDistrictManagers.isLoading) {
      cityDistrictManagers.onLoad();
    }
  }, [cityDistrictManagers]);

  return (
    <div className="spo-m-admin-school-details">
      <div className="row">
        {BRANCH_FIELDS.map((field) => (
          <AdminSchoolDetailsField
            key={`${field.field}_${params.branchId}`}
            resource={resource}
            field={field}
            value={data?.[field.field]}
            agodiValue={agodiData?.[field.field]}
            error={error?.[field.field]}
            onVisibilityChange={handleVisibilityChange}
            isManual={metaData?.isManual}
            isSubmitting={isSubmitting}
            options={get(selectFieldsOptions, field.field, [])}
          />
        ))}
      </div>
      {metaData?.isManual && canDelete && (
        <div className="u-flex">
          <Button className="u-margin-left-auto u-margin-top-md" type="danger" outline onClick={deleteModal.onOpen}>
            Verwijderen
          </Button>
          <DeleteWarningModal
            {...deleteModal}
            warningText="Ben je zeker dat je deze vestiging wilt verwijderen? Alle informatie die hieraan gekoppeld is zal verloren gaan."
            onConfirm={handleDelete}
          />
        </div>
      )}
    </div>
  );
};

export default AdminBranchDetails;
